import Fuse from 'fuse.js';
import {
  AvailableTableDisabledReason,
  type AvailableSourceTableDto,
} from '@/api/control/missionImpactAPI.schemas';
import { exceedsIdentifierLimit } from '@/lib/tablePrefix';
import type { SelectedTableConfig } from '@/lib/sourceTableSelection';

/**
 * Why a pasted table name matched a table that cannot be selected: the server-provided disabled
 * reason, or the client-side check that the prefixed staging name is too long.
 */
export type NotSyncableReason = AvailableTableDisabledReason | 'NameTooLongForPrefix';

/**
 * The outcome of resolving a pasted list of table logical names against the source's tables.
 */
export interface TableListResolution {
  /** Matched, selectable tables that are not yet selected; these should be selected. */
  toSelect: AvailableSourceTableDto[];
  /** Logical names of matched tables that were already selected. */
  alreadySelected: string[];
  /** Logical names of matched tables already configured on the source. */
  alreadyAdded: string[];
  /** Matched tables that cannot be synced, with the reason. */
  notSyncable: { logicalName: string; reason: NotSyncableReason }[];
  /** Pasted names with no matching logical name, with the closest logical name when there is one. */
  notFound: { name: string; suggestion?: string }[];
}

/** Characters stripped from the ends of each pasted token (quotes, brackets, stray punctuation). */
const WRAPPING_CHARACTERS = /^["'`[\](){}.]+|["'`[\](){}.]+$/g;

/**
 * Splits pasted text into table names. Accepts names separated by new lines, commas, semicolons,
 * tabs, spaces or pipes (a spreadsheet column, a SQL IN list, or a plain list), strips wrapping
 * quotes and brackets, drops empty entries, and removes case-insensitive duplicates, keeping the
 * first spelling.
 */
export function parseTableList(text: string): string[] {
  const seen = new Set<string>();
  const names: string[] = [];

  for (const token of text.split(/[\s,;|]+/)) {
    const name = token.replace(WRAPPING_CHARACTERS, '');
    const key = name.toLowerCase();
    if (name && !seen.has(key)) {
      seen.add(key);
      names.push(name);
    }
  }

  return names;
}

/**
 * Formats logical names as a comma-separated list, sorted case-insensitively, that
 * {@link parseTableList} reads back unchanged.
 */
export function formatTableList(names: Iterable<string>): string {
  return Array.from(names)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
    .join(', ');
}

/**
 * Resolves pasted table names against the source's tables by logical name, case-insensitively.
 * Each name is either selected (matched and selectable), already selected, already added to the
 * source, not syncable (with the reason), or not found (with the closest logical name as a
 * suggestion, when one is close enough).
 *
 * @param names Table names parsed from the pasted text.
 * @param tables All of the source's tables, including those already added (disabled reason
 *   `AlreadyAdded`).
 * @param selection The current selection, keyed by logical name.
 * @param tablePrefix The target's table prefix, used for the staging-name length check.
 */
export function resolveTableList(
  names: readonly string[],
  tables: readonly AvailableSourceTableDto[],
  selection: ReadonlyMap<string, SelectedTableConfig>,
  tablePrefix: string,
): TableListResolution {
  const byLogicalName = new Map(tables.map((table) => [table.logicalName.toLowerCase(), table]));
  const fuse = new Fuse(tables, { keys: ['logicalName'], threshold: 0.3 });
  const resolution: TableListResolution = {
    toSelect: [],
    alreadySelected: [],
    alreadyAdded: [],
    notSyncable: [],
    notFound: [],
  };

  for (const name of names) {
    const table = byLogicalName.get(name.toLowerCase());

    if (!table) {
      resolution.notFound.push({ name, suggestion: fuse.search(name, { limit: 1 })[0]?.item.logicalName });
    } else if (table.disabledReason === AvailableTableDisabledReason.AlreadyAdded) {
      resolution.alreadyAdded.push(table.logicalName);
    } else if (!table.isEnabled) {
      resolution.notSyncable.push({
        logicalName: table.logicalName,
        reason: table.disabledReason ?? AvailableTableDisabledReason.NotSupported,
      });
    } else if (exceedsIdentifierLimit(tablePrefix, table.logicalName)) {
      resolution.notSyncable.push({ logicalName: table.logicalName, reason: 'NameTooLongForPrefix' });
    } else if (selection.has(table.logicalName)) {
      resolution.alreadySelected.push(table.logicalName);
    } else {
      resolution.toSelect.push(table);
    }
  }

  return resolution;
}
